import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Workday } from '../../api/types';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { AsyncButton } from '../../components/AsyncButton';
import { MapPreview } from '../../components/MapPreview';
import { Calendar, type CalendarEvent, type CalendarView } from '../../components/calendar/Calendar';
import { useInterval } from '../../hooks/useInterval';
import { fmtDateISO, fmtTime, ymdLocal } from '../../utils/time';
import { buildSegments, computeDurationsClient } from '../../utils/workday';

function isoYYYYMMDD(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeekMonday(d: Date) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeekMonday(d: Date) {
  const s = startOfWeekMonday(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return e;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function monthGridRange(d: Date) {
  const first = startOfMonth(d);
  const start = startOfWeekMonday(first);
  const last = endOfMonth(d);
  const end = new Date(last);
  // align to Sunday of the last week
  const day = (end.getDay() + 6) % 7;
  end.setDate(end.getDate() + (6 - day));
  end.setHours(0, 0, 0, 0);
  return { start, end };
}

function rangeForView(view: CalendarView, baseDate: Date) {
  if (view === 'week') {
    const s = startOfWeekMonday(baseDate);
    const e = endOfWeekMonday(baseDate);
    return { from: isoYYYYMMDD(s), to: isoYYYYMMDD(e) };
  }
  // month
  const r = monthGridRange(baseDate);
  return { from: isoYYYYMMDD(r.start), to: isoYYYYMMDD(r.end) };
}

function splitDateTime(iso: string | null) {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function combineDateTime(date: string, time: string) {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}:00`);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

export function AdminDashboardPage() {
  const { push } = useToast();
  const [counts, setCounts] = useState<any>(null);
  const [view, setView] = useState<CalendarView>('week');
  const [baseDate, setBaseDate] = useState(() => new Date());
  const [items, setItems] = useState<Workday[]>([]);
  const [nowTick, setNowTick] = useState(new Date());

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Workday | null>(null);

  // edit fields (date + time separated)
  const [swD, setSwD] = useState('');
  const [swT, setSwT] = useState('');
  const [sbD, setSbD] = useState('');
  const [sbT, setSbT] = useState('');
  const [ebD, setEbD] = useState('');
  const [ebT, setEbT] = useState('');
  const [ewD, setEwD] = useState('');
  const [ewT, setEwT] = useState('');

  const range = useMemo(() => rangeForView(view, baseDate), [view, baseDate]);

  async function loadCounts() {
    try {
      const r = await api.get<any>('/api/admin/dashboard');
      setCounts(r?.counts || null);
    } catch (_) {}
  }

  async function loadWorkdays() {
    try {
      const r = await api.get<{ items: Workday[] }>(`/api/admin/workdays?from=${range.from}&to=${range.to}`);
      setItems(r.items || []);
    } catch (e: any) {
      push({ title: 'Errore calendario', body: e?.data?.error || e?.message });
    }
  }

  useEffect(() => {
    loadCounts();
    loadWorkdays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  useInterval(() => setNowTick(new Date()), 1000);
  useInterval(() => {
    loadCounts();
    loadWorkdays();
  }, 15000);

  const events = useMemo(() => {
    const evs: CalendarEvent<Workday>[] = [];
    const now = nowTick;
    for (const w of items) {
      const name = w.user ? `${w.user.first_name} ${w.user.last_name}` : 'Dipendente';
      const segs = buildSegments(w, now);
      for (const s of segs) {
        evs.push({
          id: s.id,
          title: s.kind === 'BREAK' ? 'Pausa' : name,
          subtitle: s.kind === 'BREAK' ? name : undefined,
          kind: s.kind,
          start: s.start,
          end: s.end,
          data: w
        });
      }
    }
    return evs;
  }, [items, nowTick]);

  function openWorkday(w: Workday) {
    setSelected(w);
    const a = splitDateTime(w.start_work_at);
    const b = splitDateTime(w.start_break_at);
    const c = splitDateTime(w.end_break_at);
    const d = splitDateTime(w.end_work_at);
    setSwD(a.date); setSwT(a.time);
    setSbD(b.date); setSbT(b.time);
    setEbD(c.date); setEbT(c.time);
    setEwD(d.date); setEwT(d.time);
    setOpen(true);
  }

  async function save() {
    if (!selected) return;
    try {
      const patch: any = {
        start_work_at: combineDateTime(swD, swT),
        start_break_at: combineDateTime(sbD, sbT),
        end_break_at: combineDateTime(ebD, ebT),
        end_work_at: combineDateTime(ewD, ewT)
      };
      // empty -> null
      for (const k of Object.keys(patch)) {
        if (patch[k] === null) patch[k] = null;
      }
      const r = await api.patch<{ workday: Workday }>(`/api/admin/workdays/${selected._id}`, patch);
      push({ title: 'Timbratura aggiornata' });
      setSelected(r.workday);
      setOpen(false);
      await loadWorkdays();
      await loadCounts();
    } catch (e: any) {
      push({ title: 'Errore salvataggio', body: e?.data?.error || e?.message });
    }
  }

  const title = useMemo(() => {
    if (view === 'week') {
      const s = startOfWeekMonday(baseDate);
      const e = endOfWeekMonday(baseDate);
      return `Settimana ${isoYYYYMMDD(s)} → ${isoYYYYMMDD(e)}`;
    }
    return baseDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  }, [view, baseDate]);

  return (
    <div className="grid" style={{ gap: 16 }}>
      {counts ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          <div className="card"><div className="small">In lavoro</div><div className="h2" style={{ margin: 0 }}>{counts.inWork}</div></div>
          <div className="card"><div className="small">In pausa</div><div className="h2" style={{ margin: 0 }}>{counts.inBreak}</div></div>
          <div className="card"><div className="small">Straordinario</div><div className="h2" style={{ margin: 0 }}>{counts.overtime}</div></div>
          <div className="card"><div className="small">Dipendenti</div><div className="h2" style={{ margin: 0 }}>{counts.employees}</div></div>
        </div>
      ) : null}

      <div className="card calCard">
        <Calendar
          view={view}
          baseDate={baseDate}
          onViewChange={setView}
          onBaseDateChange={setBaseDate}
          events={events}
          title={title}
          onSelectEvent={(e) => openWorkday(e.data as Workday)}
        />
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={selected ? `${selected.user ? `${selected.user.first_name} ${selected.user.last_name}` : 'Dipendente'} · ${fmtDateISO(selected.work_date)}` : 'Dettaglio'}
      >
                {selected ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="card" style={{ background: 'var(--gray-200)', boxShadow: 'none' }}>
              <div className="spread">
                <div>
                  <div className="small">Netto (calcolato live)</div>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>
                    {(() => {
                      const d = computeDurationsClient(selected, nowTick);
                      const h = String(Math.floor(d.netMin / 60)).padStart(2, '0');
                      const m = String(d.netMin % 60).padStart(2, '0');
                      return `${h}:${m}`;
                    })()}
                  </div>
                </div>
                <div className="badge badgeGray">ID {selected._id.slice(-6)}</div>
              </div>
            </div>

            <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
              <div className="h3" style={{ marginTop: 0 }}>Modifica timbrature</div>

              <div style={{ display: 'grid', gap: 10 }}>
                <RowEdit label="Inizio lavoro" date={swD} time={swT} onDate={setSwD} onTime={setSwT} locObj={selected.start_work_loc} />
                <RowEdit label="Inizio pausa" date={sbD} time={sbT} onDate={setSbD} onTime={setSbT} locObj={selected.start_break_loc} />
                <RowEdit label="Fine pausa" date={ebD} time={ebT} onDate={setEbD} onTime={setEbT} locObj={selected.end_break_loc} />
                <RowEdit label="Fine lavoro" date={ewD} time={ewT} onDate={setEwD} onTime={setEwT} locObj={selected.end_work_loc} />
              </div>

              <div className="spread" style={{ marginTop: 12 }}>
                <button className="btn btnSecondary" onClick={() => setOpen(false)}>Chiudi</button>
                <AsyncButton className="btn btnPrimary" onClickAsync={save} busyText="Salvo…">Salva</AsyncButton>
              </div>

              <div className="small" style={{ marginTop: 8 }}>
                Gli indirizzi (e la mappa) sono mostrati vicino a ciascun evento per una lettura più chiara.
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function RowEdit(props: {
  label: string;
  date: string;
  time: string;
  onDate: (v: string) => void;
  onTime: (v: string) => void;
  locObj?: any | null;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 10, alignItems: 'center' }}>
      <div style={{ fontWeight: 900 }}>{props.label}</div>
      <input className="input" type="date" value={props.date} onChange={(e) => props.onDate(e.target.value)} />
      <input className="input" type="time" value={props.time} onChange={(e) => props.onTime(e.target.value)} />
      <div style={{ gridColumn: '1 / -1' }}>
        <div className="small">{props.locObj?.address ? `Posizione: ${props.locObj.address}` : 'Posizione: —'}</div>
        {props.locObj ? (
          <div style={{ marginTop: 8 }}>
            <MapPreview loc={props.locObj} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
